import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { useAuth } from '../stores/auth';
import { useShowPhi } from '../hooks/useFeatureFlags';
import { authedFetch, supabase } from '../lib/supabase';
import { PageHeader } from '../components/PageHeader';
import { StatusPill } from '../components/StatusPill';
import { LoadingScreen, EmptyState } from '../components/States';
import {
  CalendarDays, Users, FileWarning, TrendingUp, DollarSign, Plus, ChevronRight,
  UserPlus, Shield, Upload, Wallet, ChevronDown, ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { phiText, show_phi } from '../lib/phiConfig';

function KpiCard({ icon: Icon, label, value, tint = 'teal', testid, trend }) {
  const tints = {
    teal: { bg: 'rgba(13,140,140,0.10)', fg: '#0B6F6F' },
    navy: { bg: 'rgba(15,42,74,0.06)', fg: '#0F2A4A' },
    coral: { bg: 'rgba(232,115,90,0.10)', fg: '#8A3B2B' },
  };
  const t = tints[tint];
  return (
    <div data-testid={testid} className="to-card to-card-hover p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: t.bg, color: t.fg }}>
          <Icon size={18} />
        </div>
        {trend && <span className="text-xs font-medium text-[var(--color-muted)]">{trend}</span>}
      </div>
      <div className="text-2xl sm:text-3xl font-semibold font-display text-[var(--color-ink)] leading-tight">{value}</div>
      <div className="text-xs sm:text-sm text-[var(--color-muted)] mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { profile, org } = useAuth();
  const showPhi = useShowPhi(); // master PHI toggle — fail-closed
  // Patient-level widgets (appointment list, New Patient, billing cards) show only when PHI is fully on.
  const phiOn = show_phi && showPhi;

  const kpiQ = useQuery({
    queryKey: ['kpis', org?.id],
    queryFn: () => authedFetch(`/kpi/dashboard/${org.id}`),
    enabled: !!org?.id,
  });

  const apptsQ = useQuery({
    queryKey: ['todays-appointments', org?.id],
    queryFn: async () => {
      const today = new Date();
      const start = new Date(today.setHours(0, 0, 0, 0)).toISOString();
      const end = new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
      const { data } = await supabase
        .from('appointments')
        .select('id, scheduled_at, duration_minutes, status, visit_type, location_address, patient_id, therapist_id, patients(first_name, last_name), user_profiles!appointments_therapist_id_fkey(full_name)')
        .eq('org_id', org.id)
        .gte('scheduled_at', start)
        .lte('scheduled_at', end)
        .order('scheduled_at');
      return data || [];
    },
    enabled: !!org?.id,
  });

  const expiringQ = useQuery({
    queryKey: ['expiring-docs', org?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('documents')
        .select('id, file_name, category, expiry_date, status, owner_id, owner_type')
        .eq('org_id', org.id)
        .in('status', ['expiring_soon', 'expired'])
        .order('expiry_date', { ascending: true })
        .limit(5);
      return data || [];
    },
    enabled: !!org?.id,
  });

  // ---- Org Admin operational dashboard extensions ----------------
  // The queries below are enabled ONLY for org_admin so the existing
  // therapist / VOM dashboard behaviour is untouched.  Each hits an
  // endpoint that already exists in the codebase; nothing new is built.
  const isOrgAdmin = profile?.role === 'org_admin';
  const now = new Date();
  const curYear = now.getUTCFullYear();
  const curMonth = now.getUTCMonth() + 1;

  // Custom month picker.  Default is the current month; the admin can pick
  // any of the last 12 months to review historical performance without
  // guessing what "this month" means.  Sections that are inherently
  // point-in-time (A/R aging, open denials, Today at a Glance) get either
  // hidden or a "current snapshot" caveat when a prior month is selected.
  const monthOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < 13; i++) {
      const d = new Date(Date.UTC(curYear, curMonth - 1 - i, 1));
      opts.push({
        value: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        label: d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
      });
    }
    return opts;
  }, [curYear, curMonth]);
  const [selectedPeriod, setSelectedPeriod] = useState(monthOptions[0].value);
  const selectedOpt = monthOptions.find(o => o.value === selectedPeriod) || monthOptions[0];
  const isCurrentMonth = selectedOpt.year === curYear && selectedOpt.month === curMonth;
  const priorYear  = selectedOpt.month === 1 ? selectedOpt.year - 1 : selectedOpt.year;
  const priorMonth = selectedOpt.month === 1 ? 12 : selectedOpt.month - 1;

  const therapistBreakdownQ = useQuery({
    queryKey: ['therapist-breakdown', org?.id, selectedOpt.year, selectedOpt.month],
    queryFn: () => authedFetch(
      `/kpi/therapist-breakdown?org_id=${org.id}&year=${selectedOpt.year}&month=${selectedOpt.month}`
    ),
    enabled: !!org?.id && isOrgAdmin,
    staleTime: 60 * 1000,
  });

  const arQ = useQuery({
    queryKey: ['ar-dashboard', org?.id],
    queryFn: () => authedFetch(`/billing/ar-dashboard?org_id=${org.id}`),
    enabled: !!org?.id && isOrgAdmin,
    staleTime: 60 * 1000,
  });

  const denialsQ = useQuery({
    queryKey: ['denials', org?.id],
    queryFn: () => authedFetch(`/kpi/denials?org_id=${org.id}`),
    enabled: !!org?.id && isOrgAdmin,
    staleTime: 60 * 1000,
  });

  // Selected-period billed/collected — computed client-side via Supabase so
  // we do NOT touch the working /kpi/dashboard endpoint (which only knows
  // the current period).  This same pattern already powers the prior-month
  // delta below.
  const periodTotalsQ = useQuery({
    queryKey: ['period-totals', org?.id, selectedOpt.year, selectedOpt.month],
    queryFn: async () => {
      const start = new Date(Date.UTC(selectedOpt.year, selectedOpt.month - 1, 1)).toISOString();
      const endD  = new Date(Date.UTC(selectedOpt.year, selectedOpt.month,     1)).toISOString();
      const { data } = await supabase
        .from('appointments')
        .select('rate_charged, amount_collected, patient_id')
        .eq('org_id', org.id)
        .gte('scheduled_at', start)
        .lt('scheduled_at', endD);
      const list = data || [];
      const billed = list.reduce((s, r) => s + Number(r.rate_charged || 0), 0);
      const collected = list.reduce((s, r) => s + Number(r.amount_collected || 0), 0);
      const uniquePatients = new Set(list.map(r => r.patient_id).filter(Boolean)).size;
      return {
        billed, collected,
        rate: billed > 0 ? (collected / billed) * 100 : 0,
        unique_patients: uniquePatients,
        visits: list.length,
      };
    },
    enabled: !!org?.id && isOrgAdmin,
    staleTime: 60 * 1000,
  });

  const priorPeriodQ = useQuery({
    queryKey: ['prior-collection-rate', org?.id, priorYear, priorMonth],
    queryFn: async () => {
      const start = new Date(Date.UTC(priorYear, priorMonth - 1, 1)).toISOString();
      const endD  = new Date(Date.UTC(priorYear, priorMonth,     1)).toISOString();
      const { data } = await supabase
        .from('appointments')
        .select('rate_charged, amount_collected')
        .eq('org_id', org.id)
        .gte('scheduled_at', start)
        .lt('scheduled_at', endD);
      const list = data || [];
      const billed = list.reduce((s, r) => s + Number(r.rate_charged || 0), 0);
      const collected = list.reduce((s, r) => s + Number(r.amount_collected || 0), 0);
      return { billed, collected, rate: billed > 0 ? (collected / billed) * 100 : 0 };
    },
    enabled: !!org?.id && isOrgAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // Derive today-at-a-glance counters from the appointments list we
  // already fetch — no extra network cost.  Must run before the early
  // return below to satisfy the rules-of-hooks lint check.
  const todayStats = useMemo(() => {
    const list = apptsQ.data || [];
    return {
      scheduled: list.length,
      completed: list.filter(a => a.status === 'completed').length,
      cancelled: list.filter(a => a.status === 'cancelled').length,
      no_show:   list.filter(a => a.status === 'no_show').length,
    };
  }, [apptsQ.data]);

  if (!org) {
    return (
      <div>
        <PageHeader title="Dashboard" subtitle="—" />
        <div className="p-4"><LoadingScreen /></div>
      </div>
    );
  }

  const k = kpiQ.data || {};
  const isVom = profile?.role === 'virtual_ops_manager';
  const isTherapist = profile?.role === 'therapist';
  const hideFinancials = isVom || isTherapist;

  const priorRate = priorPeriodQ.data?.rate;
  // When viewing the current month, prefer the working /kpi/dashboard totals
  // (they include today's not-yet-persisted appointment amounts).  For any
  // prior month, fall back to the client-computed period totals.
  const dispBilled    = isCurrentMonth ? (k.month_total_billed    ?? 0) : (periodTotalsQ.data?.billed    ?? 0);
  const dispCollected = isCurrentMonth ? (k.month_total_collected ?? 0) : (periodTotalsQ.data?.collected ?? 0);
  const dispRate      = isCurrentMonth ? (k.collection_rate       ?? 0) : (periodTotalsQ.data?.rate      ?? 0);
  const dispActive    = isCurrentMonth ? (k.active_patients ?? 0)       : (periodTotalsQ.data?.unique_patients ?? 0);
  const dispPeriod    = isCurrentMonth ? (k.current_period)             : `${selectedOpt.year}-${String(selectedOpt.month).padStart(2, '0')}`;
  const rateDelta = priorRate != null ? +(dispRate - priorRate).toFixed(1) : null;

  return (
    <div>
      <PageHeader
        title={`Good ${getGreeting()}, ${profile?.full_name?.split(' ')[0] || 'there'}`}
        subtitle={org?.name}
        actions={
          <div className="flex items-center gap-2">
            {isOrgAdmin && (
              <label className="inline-flex items-center gap-1.5" title="Change the reporting period">
                <span className="text-[11px] text-[var(--color-muted)] hidden sm:inline">Period</span>
                <select
                  className="input text-xs py-1"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  data-testid="dashboard-period-picker"
                >
                  {monthOptions.map((o, i) => (
                    <option key={o.value} value={o.value}>
                      {i === 0 ? `This month · ${o.label}` : o.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isVom && phiOn && (
              <Link to="/patients?action=new" className="btn btn-primary text-sm hidden sm:inline-flex" data-testid="dashboard-new-patient-btn">
                <Plus size={16} /> New Patient
              </Link>
            )}
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5 sm:py-6 space-y-5">
        {/* KPI Grid */}
        <div className={`grid grid-cols-2 ${phiOn ? 'lg:grid-cols-4' : show_phi ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-3 sm:gap-4 fade-up`}>
          {phiOn && <KpiCard icon={CalendarDays} label="Appointments today" value={k.appointments_today ?? '—'} tint="teal" testid="kpi-appointments-today" />}
          {show_phi && <KpiCard icon={Users} label="Active patients" value={k.active_patients ?? '—'} tint="navy" testid="kpi-active-patients" />}
          <KpiCard icon={UserPlus} label="Open leads" value={k.leads ?? '—'} tint="coral" testid="kpi-leads" />
          <KpiCard icon={FileWarning} label="Doc alerts" value={k.expiring_documents ?? '—'} tint="coral" testid="kpi-doc-alerts" />
        </div>

        {/* Financial row — hidden for VOM and therapist roles */}
        {phiOn && !hideFinancials && k.month_total_billed !== undefined && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 fade-up-delay-1">
            <KpiCard icon={DollarSign} label={`Billed (${k.current_period})`} value={`$${(k.month_total_billed || 0).toLocaleString()}`} tint="navy" testid="kpi-total-billed" />
            <KpiCard icon={DollarSign} label={`Collected (${k.current_period})`} value={`$${(k.month_total_collected || 0).toLocaleString()}`} tint="teal" testid="kpi-total-collected" />
            <KpiCard icon={TrendingUp} label="Collection rate" value={`${(k.collection_rate || 0).toFixed(1)}%`} tint="teal" testid="kpi-collection-rate" />
          </div>
        )}

        {isVom && (
          <div className="to-card p-4 flex items-center gap-3 text-sm text-[var(--color-slate)] border-[rgba(15,42,74,0.18)]" data-testid="vom-financial-notice">
            <Shield size={16} className="text-[var(--color-teal)] shrink-0" />
            <span>Operations View — financial KPIs are restricted for VOMs per role policy.</span>
          </div>
        )}

        {/* -------- Org-admin operational dashboard sections -------- */}
        {isOrgAdmin && (
          <>
            {isCurrentMonth && (
              <TodayAtAGlance phiOn={phiOn}
                today={todayStats}
                docAlerts={expiringQ.data?.length || 0}
                denialsCount={(denialsQ.data?.denials || []).filter(d => d.status === 'pending').length}
                activePatients={k.active_patients}
                leads={k.leads}
              />
            )}

            <ThisMonthPerformance
              period={dispPeriod}
              periodLabel={selectedOpt.label}
              isCurrent={isCurrentMonth}
              billed={dispBilled}
              collected={dispCollected}
              collectionRate={dispRate}
              rateDelta={rateDelta}
              ar={arQ.data}
              denials={denialsQ.data?.denials || []}
              activePatients={dispActive}
            />

            <TeamPerformance therapists={therapistBreakdownQ.data?.therapists || []}
                             isLoading={therapistBreakdownQ.isLoading}
                             periodLabel={selectedOpt.label}
                             isCurrent={isCurrentMonth} />

            <QuickActions />
          </>
        )}

        {/* Today's appointments — hidden unless PHI display is on */}
        {phiOn && <section className="fade-up-delay-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold">Today’s appointments</h2>
            <Link to="/appointments" className="text-xs font-medium text-[var(--color-teal)] hover:underline inline-flex items-center gap-1" data-testid="dashboard-view-all-appts">
              View all <ChevronRight size={14} />
            </Link>
          </div>
          {apptsQ.isLoading ? <LoadingScreen /> : (apptsQ.data?.length || 0) === 0 ? (
            <EmptyState icon={CalendarDays} title="No appointments today" body="Take a moment to plan your week or add a new appointment." action={<Link className="btn btn-primary text-sm mt-2" to="/appointments?action=new" data-testid="empty-add-appt">Add appointment</Link>} />
          ) : (
            <div className="space-y-2.5">
              {apptsQ.data.map((a, idx) => (
                <Link
                  key={a.id}
                  to={`/appointments/${a.id}`}
                  data-testid="appointment-row"
                  className="to-card to-card-hover flex items-center gap-3 p-3.5 sm:p-4"
                >
                  <div className="h-12 w-14 rounded-xl bg-[rgba(13,140,140,0.10)] text-[#0B6F6F] flex flex-col items-center justify-center shrink-0">
                    <div className="text-[10px] uppercase tracking-wider">{new Date(a.scheduled_at).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' }).split(' ')[1] || 'AM'}</div>
                    <div className="text-sm font-semibold">{new Date(a.scheduled_at).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' }).split(' ')[0]}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[var(--color-ink)] truncate">{showPhi ? `${a.patients?.first_name || ''} ${a.patients?.last_name || ''}` : `Visit ${idx + 1}`}</div>
                    <div className="text-xs text-[var(--color-muted)] truncate">{a.visit_type?.replace('_', ' ')} · {a.duration_minutes}min · {a.user_profiles?.full_name || ''}</div>
                  </div>
                  <StatusPill status={a.status} />
                </Link>
              ))}
            </div>
          )}
        </section>}

        {/* Expiring documents */}
        {expiringQ.data?.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-semibold">Document alerts</h2>
              <Link to="/documents" className="text-xs font-medium text-[var(--color-teal)] hover:underline" data-testid="dashboard-view-all-docs">View all</Link>
            </div>
            <div className="to-card divide-y divide-[var(--color-border)]">
              {expiringQ.data.map(d => (
                <div key={d.id} className="flex items-center justify-between gap-3 p-3.5" data-testid="document-alert-row">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{d.file_name}</div>
                    <div className="text-xs text-[var(--color-muted)] capitalize">{d.category.replace(/_/g, ' ')} · expires {d.expiry_date}</div>
                  </div>
                  <StatusPill status={d.status} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

// ---------------------------------------------------------------------------
// Local MetricCard / ChartCard — deliberate inline copies of the components
// already used on /kpi so the look and feel stays 1:1 without introducing a
// cross-page import (which would require refactoring KpiDashboard.jsx to
// export them).  Kept intentionally tiny.
// ---------------------------------------------------------------------------
function MetricCard({ label, value, sub, tone = 'normal', testid, delta }) {
  const cls = tone === 'warn' ? 'text-[#991B1B]' : tone === 'good' ? 'text-[#0F7B57]' : 'text-[var(--color-ink)]';
  const deltaTone = delta == null ? '' : delta > 0 ? 'text-[#0F7B57]' : delta < 0 ? 'text-[#8A3B2B]' : 'text-[var(--color-muted)]';
  const deltaText = delta == null ? null : `${delta > 0 ? '+' : ''}${delta}${sub?.includes('%') ? '' : ''} vs last mo`;
  return (
    <div className="to-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted)]">{label}</div>
      <div className={`text-2xl font-display font-semibold mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] text-[var(--color-muted)] mt-1">{sub}</div>}
      {deltaText && <div className={`text-[10.5px] mt-1 ${deltaTone}`}>{deltaText}</div>}
    </div>
  );
}

function ChartCard({ title, children, testid, action }) {
  return (
    <div className="to-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wider text-[var(--color-muted)] font-semibold">{title}</h4>
        {action}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Today at a Glance
// ---------------------------------------------------------------------------
function TodayAtAGlance({ today, docAlerts, denialsCount, activePatients, leads, phiOn }) {
  const alertCount = (docAlerts || 0) + (denialsCount || 0);
  return (
    <section className="fade-up" data-testid="dashboard-today-glance">
      <h2 className="font-display text-lg font-semibold mb-3">Today at a glance</h2>
      <div className={`grid ${phiOn ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : show_phi ? 'grid-cols-2' : 'grid-cols-1'} gap-2 sm:gap-3`}>
        {phiOn && <><MetricCard label="Scheduled today"     value={today.scheduled}                 testid="today-scheduled" />
        <MetricCard label="Completed"           value={today.completed} tone="good"     testid="today-completed" />
        <MetricCard label="Cancelled / no-show" value={today.cancelled + today.no_show}
                    tone={(today.cancelled + today.no_show) > 0 ? 'warn' : 'normal'}   testid="today-cancelled" /></>}
        {show_phi && <MetricCard label="Active patients"     value={activePatients ?? '\u2014'}      testid="today-active"    sub={`${leads ?? 0} open leads`} />}
        <MetricCard label="Pending alerts"      value={alertCount} tone={alertCount ? 'warn' : 'normal'}
                    testid="today-alerts"
                    sub={`${docAlerts} document · ${denialsCount} denial`} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: This Month Performance
// ---------------------------------------------------------------------------
function ThisMonthPerformance({ period, periodLabel, isCurrent, billed, collected, collectionRate, rateDelta, ar, denials, activePatients }) {
  const b = ar?.buckets || {};
  const totalAr = ar?.total_outstanding || 0;
  const bucketRows = [
    { label: 'Current',   amount: b.current || 0,  tone: 'text-[#0F7B57]' },
    { label: '1–30 days', amount: b['1_30']  || 0, tone: 'text-[var(--color-ink)]' },
    { label: '31–60',     amount: b['31_60'] || 0, tone: 'text-[#B45309]' },
    { label: '61–90',     amount: b['61_90'] || 0, tone: 'text-[#B45309]' },
    { label: '90+',       amount: b['90_plus'] || 0, tone: 'text-[#991B1B]' },
  ];
  const denialAmt = denials.reduce((s, d) => s + Number(d.amount || 0), 0);
  const denialCount = denials.length;
  const denialRate = billed > 0 ? (denialAmt / billed) * 100 : 0;

  const headerTitle = isCurrent
    ? `This month ${period ? `(${period})` : ''}`
    : `Performance for ${periodLabel}`;
  // A/R aging and open denials are inherently point-in-time — we don't have
  // a historical snapshot table, so we surface a small caveat when the
  // admin is looking at a prior month.
  const snapshotCaveat = !isCurrent && (
    <span className="text-[10.5px] text-[var(--color-muted)] italic" title="A/R aging and open denials are current-day snapshots — they cannot be recomputed for a prior month.">
      current snapshot
    </span>
  );

  return (
    <section className="fade-up-delay-1" data-testid="dashboard-month-perf">
      <h2 className="font-display text-lg font-semibold mb-3" data-testid="dashboard-month-title">{headerTitle}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className={`grid ${show_phi ? 'grid-cols-2' : 'grid-cols-3'} gap-3`}>
          <MetricCard label="Billed"         value={`$${(billed || 0).toLocaleString()}`}     testid="month-billed" />
          <MetricCard label="Collected"      value={`$${(collected || 0).toLocaleString()}`}  testid="month-collected" tone="good" />
          <MetricCard label="Collection rate" value={`${collectionRate.toFixed(1)}%`} testid="month-collection-rate" delta={rateDelta} sub="%" />
          {show_phi && <MetricCard label={isCurrent ? 'Active patients' : 'Patients seen'} value={activePatients} testid="month-active" />}
        </div>

        <ChartCard
          title="A/R aging"
          testid="month-ar-card"
          action={
            <div className="flex items-center gap-2">
              {snapshotCaveat}
              <Link to="/kpi/ar" className="text-[11px] text-[var(--color-teal)] hover:underline">View</Link>
            </div>
          }
        >
          <div className="text-xl font-display font-semibold text-[var(--color-ink)] mb-2">
            ${totalAr.toLocaleString()}<span className="text-[10.5px] text-[var(--color-muted)] font-normal ml-1">outstanding</span>
          </div>
          <ul className="space-y-1" data-testid="ar-buckets">
            {bucketRows.map(r => (
              <li key={r.label} className="flex items-center gap-2 text-[12px]">
                <span className="w-16 text-[var(--color-muted)]">{r.label}</span>
                <div className="flex-1 h-1.5 rounded-full bg-[var(--color-border)] overflow-hidden">
                  <div className="h-full bg-[var(--color-teal)]" style={{ width: totalAr > 0 ? `${(r.amount / totalAr) * 100}%` : '0%' }} />
                </div>
                <span className={`font-mono w-16 text-right ${r.tone}`}>${Math.round(r.amount).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </ChartCard>

        <ChartCard
          title="Denials"
          testid="month-denials-card"
          action={
            <div className="flex items-center gap-2">
              {snapshotCaveat}
              <Link to="/kpi/import" className="text-[11px] text-[var(--color-teal)] hover:underline">Details</Link>
            </div>
          }
        >
          <div className="grid grid-cols-2 gap-2">
            <MetricCard label="Denial rate"   value={`${denialRate.toFixed(1)}%`} testid="month-denial-rate" tone={denialRate > 5 ? 'warn' : 'normal'} />
            <MetricCard label="Open denials"  value={denialCount} testid="month-denials-count" />
            <MetricCard label="At-risk $"     value={`$${Math.round(denialAmt).toLocaleString()}`} testid="month-denials-amt" tone={denialAmt > 0 ? 'warn' : 'normal'} />
            <MetricCard label="Write-offs 30d" value={`$${Math.round((ar?.write_offs_30d?.amount || 0)).toLocaleString()}`} testid="month-writeoffs" />
          </div>
        </ChartCard>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: Team Performance
// ---------------------------------------------------------------------------
function TeamPerformance({ therapists, isLoading, periodLabel, isCurrent }) {
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => {
    const sorted = (therapists || []).slice().sort((a, b) => (b.visits ?? 0) - (a.visits ?? 0));
    return expanded ? sorted : sorted.slice(0, 5);
  }, [therapists, expanded]);
  const hasMore = (therapists || []).length > 5;

  return (
    <section className="fade-up-delay-2" data-testid="dashboard-team-perf">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="font-display text-lg font-semibold">
          Team performance
          {!isCurrent && periodLabel && (
            <span className="ml-2 text-xs font-normal text-[var(--color-muted)]">· {periodLabel}</span>
          )}
        </h2>
        <Link to="/team" className="text-xs font-medium text-[var(--color-teal)] hover:underline inline-flex items-center gap-1">
          View team <ChevronRight size={14} />
        </Link>
      </div>
      <div className="to-card overflow-x-auto" data-testid="team-perf-table">
        {isLoading ? (
          <div className="p-4"><LoadingScreen /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--color-muted)]">No visits recorded for team this month.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface-2)] text-[10px] uppercase tracking-wider text-[var(--color-muted)]">
              <tr>
                <th className="px-3 py-2 text-left">Therapist</th>
                <th className="px-3 py-2 text-center">Visits</th>
                <th className="px-3 py-2 text-center">No-show</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right hidden sm:table-cell">Collection %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(t => {
                const utilization = t.visits > 0 ? Math.min(100, Math.round((t.visits / 40) * 100)) : 0;
                const nsRate = t.visits > 0 ? ((t.no_shows / t.visits) * 100) : 0;
                return (
                  <tr key={t.therapist_id} className="border-t border-[var(--color-border)]" data-testid={`team-perf-row-${t.therapist_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-[var(--color-ink)] truncate">{t.name}</div>
                      <div className="mt-1 flex items-center gap-1.5">
                        <div className="flex-1 h-1 rounded-full bg-[var(--color-border)] overflow-hidden max-w-[120px]">
                          <div className="h-full bg-[var(--color-teal)]" style={{ width: `${utilization}%` }} />
                        </div>
                        <span className="text-[10px] text-[var(--color-muted)]">{utilization}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center font-mono">{t.visits}</td>
                    <td className={`px-3 py-2 text-center font-mono ${nsRate > 10 ? 'text-[#991B1B]' : 'text-[var(--color-ink)]'}`}>
                      {nsRate.toFixed(0)}%
                    </td>
                    <td className="px-3 py-2 text-right font-mono">${Math.round(t.collected || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right hidden sm:table-cell font-mono">{(t.collection_rate || 0).toFixed(0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="w-full border-t border-[var(--color-border)] py-2 text-xs text-[var(--color-teal)] hover:bg-[var(--color-surface-2)] inline-flex items-center justify-center gap-1"
            data-testid="team-perf-expand"
          >
            {expanded ? 'Show less' : `Show all ${therapists.length}`} <ChevronDown size={12} className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: Quick Actions
// ---------------------------------------------------------------------------
function QuickActions() {
  const items = [
    { to: '/kpi/import',  icon: Upload,       label: 'Import CSV',    desc: 'Reconcile a billing report',        testid: 'qa-import' },
    { to: '/kpi/ar',      icon: Wallet,       label: 'A/R Tracker',   desc: 'Manage outstanding balances',        testid: 'qa-ar' },
    { to: '/compliance',  icon: ShieldCheck,  label: 'Compliance',    desc: phiText('HIPAA posture & BAA', 'Compliance posture & BAA'),                testid: 'qa-compliance' },
    { to: '/team',        icon: Users,        label: 'Team',          desc: 'Therapists & credentialing',          testid: 'qa-team' },
  ];
  return (
    <section data-testid="dashboard-quick-actions">
      <h2 className="font-display text-lg font-semibold mb-3">Quick actions</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map(a => (
          <Link
            key={a.to}
            to={a.to}
            data-testid={a.testid}
            className="to-card to-card-hover p-4 flex items-start gap-3 group"
          >
            <div className="h-10 w-10 rounded-xl bg-[rgba(11,111,111,0.10)] text-[#0B6F6F] flex items-center justify-center shrink-0 group-hover:bg-[rgba(11,111,111,0.15)] transition-colors">
              <a.icon size={18} />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-[var(--color-ink)]">{a.label}</div>
              <div className="text-[11px] text-[var(--color-muted)] mt-0.5">{a.desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
